/**
 * Cost of Sales: upload a supplier invoice.
 *
 * Winnie's v7 note asks the platform to pick the details up off the invoice and record
 * the cost against the VIN, with manual entry still available. This is the front of
 * that: choose a file, see exactly what was read and what was not, then confirm it in
 * the same split form that has always refused to save until the lines add up.
 *
 * The reading is never treated as fact. Everything it found is shown as a suggestion
 * with the line it came from, and nothing reaches the ledger until a person saves.
 */

import { useState } from 'react'
import { ParsedInvoice, centsToField } from '../../lib/invoiceParse'
import { errText } from '../../lib/errText'
import FileDropZone from '../FileDropZone'
import {
  DealMatch, readInvoiceFile, saveSupplierInvoice, markAllocated, discardInvoice,
} from '../../services/supplierInvoiceService'
import SplitInvoiceForm, { SplitPrefill } from './SplitInvoiceForm'
import { formatAbn } from '../../lib/abn'
import { lookupAbn, AbrResult } from '../../services/buyerService'
import ContactSavePrompt from '../ContactSavePrompt'

interface InvoiceUploadPanelProps {
  userId: string | null
  onSaved: () => void
  onCancel: () => void
}

/**
 * A first guess at the cost category from who sent the bill. It is only a default; the
 * split form shows the dropdown and the person can change it before saving.
 */
function guessCategory(text: string): string {
  const s = text.toLowerCase()
  if (/transport|freight|tow|carrier|logistics/.test(s)) return 'transport'
  if (/inspect/.test(s)) return 'inspection'
  if (/ppsr|encumbrance/.test(s)) return 'ppsr'
  if (/recondition|detail|panel|mechanic|repair|service|tyre|windscreen/.test(s)) return 'reconditioning'
  return 'other'
}

const money = (cents: number | null) =>
  cents === null ? 'not found' : '$' + (cents / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export default function InvoiceUploadPanel({ userId, onSaved, onCancel }: InvoiceUploadPanelProps) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fileName, setFileName] = useState('')
  const [parsed, setParsed] = useState<ParsedInvoice | null>(null)
  const [matches, setMatches] = useState<Record<string, DealMatch>>({})
  const [unknownVins, setUnknownVins] = useState<string[]>([])
  const [prefill, setPrefill] = useState<SplitPrefill | null>(null)
  const [invoiceId, setInvoiceId] = useState<string | null>(null)
  const [invoicePath, setInvoicePath] = useState<string | null>(null)
  const [manual, setManual] = useState(false)
  const [prefillNote, setPrefillNote] = useState('')
  /* The name the ABR gave back for the ABN printed on the invoice, and a sentence about
     what happened if it did not. Winnie, 13 Sep: the lookup is what stops the same supplier
     arriving as "Pinacorp", "Pinacorp Pty Ltd" and "PINACORP" on three different rows. */
  const [abnName, setAbnName] = useState('')
  const [abnNote, setAbnNote] = useState('')
  const [abnResult, setAbnResult] = useState<AbrResult | null>(null)

  const pick = async (file: File | null) => {
    if (!file) return
    setBusy(true)
    setError(null)
    setParsed(null)
    setPrefill(null)
    setInvoiceId(null)
    setInvoicePath(null)
    setAbnName('')
    setAbnNote('')
    setAbnResult(null)
    setFileName(file.name)
    try {
      const result = await readInvoiceFile(file)
      setParsed(result.parsed)
      setMatches(result.matches)
      setUnknownVins(result.unknownVins)
      const draft = await saveSupplierInvoice(file, result.parsed, userId)
      setInvoiceId(draft.id)
      setInvoicePath(draft.filePath)
      /* The lookup never gates the upload. Winnie, 13 Sep: "A failed or unavailable lookup
         never blocks invoice entry... the invoice still saves - just without the name
         auto-filled, same as if no ABN had been on the document at all." */
      if (result.parsed.supplierAbn) {
        try {
          const abr = await lookupAbn(result.parsed.supplierAbn)
          setAbnResult(abr)
          if (abr.ok && abr.legalName) {
            setAbnName(abr.legalName)
            setAbnNote(abr.abnStatus ? abr.abnStatus : '')
          } else {
            setAbnNote(abr.message || 'The ABR could not be reached.')
          }
        } catch {
          setAbnNote('The ABR could not be reached just now. The ABN is still recorded.')
        }
      }
    } catch (err) {
      setError(errText(err, 'Could not read that file.'))
    } finally {
      setBusy(false)
    }
  }

  const confirm = () => {
    if (!parsed) return
    const known = parsed.lines.filter((l) => matches[l.key])
    const lineSum = known.reduce((s, l) => s + (l.amountCents || 0), 0)
    const total = parsed.totalCents
    const gst = parsed.gstCents || 0

    // An expense in this ledger is stored GST inclusive, with gst_amount being the GST
    // inside it. Most supplier invoices itemise ex GST and add the GST once at the
    // bottom, so the lines as printed would never add up to the invoice and the split
    // form would refuse to save. Where the lines clearly are the ex GST ones, gross them
    // up in proportion and put the rounding remainder on the last line, so the parts add
    // back to the invoice exactly. Where they already add to the total, leave them alone.
    let amounts = known.map((l) => l.amountCents)
    let grossedUp = false
    if (total !== null && lineSum > 0 && Math.abs(lineSum - (total - gst)) <= known.length) {
      let running = 0
      amounts = known.map((l, i) => {
        if (i === known.length - 1) return total - running
        const share = Math.round(((l.amountCents || 0) * total) / lineSum)
        running += share
        return share
      })
      grossedUp = true
    }

    const rows = known.map((l, i) => ({
      dealId: matches[l.key].id,
      amount: centsToField(amounts[i]),
      note: l.vin ? l.vin : l.stockNumber ? 'Stock ' + l.stockNumber : '',
    }))
    /* One empty line so the form has something to start from when nothing matched. It
       used to pad to two, which now reads as two line items missing a vehicle and holds
       the save shut on an invoice that was only ever about one car. */
    if (!rows.length) rows.push({ dealId: '', amount: '', note: '' })

    setPrefillNote(
      grossedUp
        ? 'The invoice itemises each vehicle before GST. Each line has been grossed up so the split adds to the invoice total; the GST is apportioned across them on save.'
        : '',
    )

    setPrefill({
      /* The registered name wins over the reader's guess at which line was the business
         name, because one is looked up and the other is a heuristic. Still editable on the
         split form - auto-fill is a default, not a lock. */
      supplier: abnName || parsed.supplier,
      supplierAbn: parsed.supplierAbn,
      invoiceRef: parsed.invoiceRef,
      incurredOn: parsed.invoiceDate || new Date().toISOString().slice(0, 10),
      category: guessCategory(parsed.supplier + ' ' + parsed.lines.map((l) => l.text).join(' ')),
      total: centsToField(parsed.totalCents),
      gstTotal: centsToField(parsed.gstCents),
      lines: rows,
      supplierInvoiceId: invoiceId,
    })
  }

  const saved = async () => {
    if (invoiceId) {
      try {
        await markAllocated(invoiceId)
      } catch {
        // The expenses rows are already in. Failing to tick the draft off is untidy,
        // not wrong, and saying so beats pretending the save failed.
        setError('Saved, but the uploaded invoice is still showing as unallocated.')
      }
    }
    onSaved()
  }

  /**
   * Walking away from an upload should not leave the file behind. The draft has not been
   * confirmed, so nothing in the accounts points at it; failing to remove it is untidy
   * rather than harmful, so it is reported and the person is still let go.
   */
  const abandon = async (then: () => void) => {
    if (invoiceId) {
      try {
        await discardInvoice(invoiceId, invoicePath)
      } catch {
        setError('The uploaded file could not be removed. It is still in storage, unallocated.')
      }
      setInvoiceId(null)
      setInvoicePath(null)
    }
    then()
  }

  if (manual) {
    return <SplitInvoiceForm userId={userId} onSaved={onSaved} onCancel={onCancel} />
  }

  if (prefill) {
    return (
      <div>
        <div className="rep-alert">
          Read from <strong>{fileName}</strong>. Check every figure before you save; nothing
          reaches the ledger until you do.
        </div>
        {prefillNote && <div className="rep-alert">{prefillNote}</div>}
        <SplitInvoiceForm
          userId={userId}
          onSaved={saved}
          onCancel={() => setPrefill(null)}
          prefill={prefill}
        />
      </div>
    )
  }

  return (
    <div className="rep-card" style={{ padding: '1rem' }}>
      <h3 style={{ marginTop: 0 }}>Upload a supplier invoice</h3>
      <p className="rep-muted" style={{ marginTop: 0 }}>
        A PDF with selectable text can be read automatically, including the VIN or stock number
        on each line. A photo or a scan cannot, and you will be asked to type it in.
      </p>

      <div className="rep-toolbar">
        <FileDropZone
          accept=".pdf,.txt,.csv,.tsv,application/pdf,text/plain,text/csv"
          label="Upload Invoice"
          busy={busy}
          busyLabel="Reading the invoice…"
          onFiles={(files) => pick(files[0])}
        />
        <button className="rep-btn" onClick={() => abandon(() => setManual(true))} disabled={busy}>
          Enter it by hand instead
        </button>
        <button className="rep-btn" onClick={() => abandon(onCancel)} disabled={busy}>
          Cancel
        </button>
      </div>

      {busy && <p className="rep-muted">Reading {fileName}...</p>}
      {error && <div className="rep-error">{error}</div>}

      {parsed && !busy && (
        <div style={{ marginTop: '1rem' }}>
          <h4 style={{ margin: '0 0 0.5rem' }}>What was read from {fileName}</h4>
          <div className="rep-table-wrap">
            <table>
              <tbody>
                <tr>
                  <td>Supplier</td>
                  <td>{abnName || parsed.supplier || 'not found'}</td>
                </tr>
                <tr>
                  <td>Supplier ABN</td>
                  <td>
                    {parsed.supplierAbn ? formatAbn(parsed.supplierAbn) : 'not found'}
                    {abnName ? ' — ' + abnName : ''}
                    {abnNote ? <span className="rep-muted"> {abnNote}</span> : null}
                    {/* Winnie, 15 Sep: the register answered for a supplier Contacts does not
                        know. Offer to keep it, so the next invoice from them needs no lookup. */}
                    <ContactSavePrompt
                      abn={parsed.supplierAbn || ''}
                      result={abnResult}
                      type="Supplier"
                      suggest={{ name: abnName || parsed.supplier || null }}
                    />
                  </td>
                </tr>
                <tr>
                  <td>Invoice number</td>
                  <td>{parsed.invoiceRef || 'not found'}</td>
                </tr>
                <tr>
                  <td>Invoice date</td>
                  <td>{parsed.invoiceDate || 'not found'}</td>
                </tr>
                <tr>
                  <td>Total</td>
                  <td>{money(parsed.totalCents)}</td>
                </tr>
                <tr>
                  <td>GST</td>
                  <td>{money(parsed.gstCents)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h4 style={{ margin: '1rem 0 0.5rem' }}>Vehicles found on the invoice</h4>
          {parsed.lines.length === 0 ? (
            <p className="rep-muted">
              No VIN or stock number appears on this invoice. You can still enter it, choosing
              the vehicles yourself.
            </p>
          ) : (
            <div className="rep-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>VIN or stock</th>
                    <th>Matched vehicle</th>
                    <th>Amount on the line</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.lines.map((l) => (
                    <tr key={l.key}>
                      <td>{l.vin || 'Stock ' + l.stockNumber}</td>
                      <td>
                        {matches[l.key] ? (
                          matches[l.key].label
                        ) : (
                          <span className="rep-bad">Not a vehicle we hold</span>
                        )}
                      </td>
                      <td>{money(l.amountCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {unknownVins.length > 0 && (
            <p className="rep-bad">
              {unknownVins.length} vehicle{unknownVins.length === 1 ? '' : 's'} on this invoice
              {unknownVins.length === 1 ? ' does' : ' do'} not match any vehicle in the platform.
              Those lines will not be filled in.
            </p>
          )}

          {parsed.notes.length > 0 && (
            <ul className="rep-muted">
              {parsed.notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          )}

          <div className="rep-toolbar">
            <button className="rep-btn" onClick={confirm}>
              Use these details
            </button>
            <button className="rep-btn" onClick={() => abandon(() => setManual(true))}>
              Start from a blank form
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
